import * as React from 'react';
import { isEqual } from 'lodash';
// # interfaces
import { iCardsApiService } from '@/interfaces/services/api/cards';
import { iPaymentCard } from '@/interfaces/payments';
import { iPromptChildProps } from '@/interfaces/ui';
// # services
import { cardsService } from '@/services/api';
// # hooks
import { useIsBusy } from '@/hooks';
// # components
import { Message, Spinner } from '@/components/feedback';
import { PaymentCard, PaymentCardForm } from '.';
import { iStripeService } from '@/interfaces/services/stripe';
// # services
import { uiService } from '@/services';

interface iProps {
    /**
     * Optionally replace the default cards service.
     */
    cardsService?: iCardsApiService;
    /**
     * Called when the user selects a card.
     * Note: the API and this component currently only supports having one card.
     */
    onCardSelected?: (card: null | iPaymentCard) => any;
    /**
     * Optionally mark a card as "selected"
     * Note: the API and this component currently only supports having one card.
     */
    selectedCard?: iPaymentCard;
    /**
     * Optionally replace the default stripe service.
     */
    stripeService?: iStripeService;
}

/**
 * Content displayed in prompt modal displayed before the user deletes a card.
 */
const DeleteCardPromptContent: React.SFC<iPromptChildProps & { card: iPaymentCard }> = ({ card, setPromptValue }) => {
    setPromptValue(true);

    return (
        <Message title={`Delete card ending in ${card.last_four}?`} theme="warning">
            If you have an active subscription, this may affect your payments.
        </Message>
    );
};

/**
 * Lets a user manage their saved payment cards.
 */
export const ManagePaymentCards: React.SFC<iProps> = (props: iProps) => {
    const cardResolver = React.useMemo(() => props.cardsService.get(), []);
    let [card, setCard] = React.useState<iPaymentCard | undefined>(undefined);
    const [isUpdatingCard, setIsUpdatingCard] = React.useState(false);
    const isLoading = useIsBusy([cardResolver]);
    React.useEffect(() => {
        // @ts-ignore
        cardResolver.then(setCard);

    }, []);

    if (card?.stripe_id == null) {
        card = null;
    }

    /**
     * Saves a new card
     *
     * @param {iPaymentCard} newCard
     * @returns {Promise<void>}
     */
    const handleNewCard = async (newCard: iPaymentCard): Promise<void> => {
        try {
            setIsUpdatingCard(true);
            const savedCard = newCard;
            setCard(savedCard);
            setIsUpdatingCard(false);
        } catch (err) {
            setIsUpdatingCard(false);
        }
    };

    /**
     * Deletes the existing card
     *
     * @returns {Promise<void>}
     */
    const handleDeleteCard = async () => {
        try {
            setIsUpdatingCard(true);
            try {
                await uiService.prompt(promptProps => <DeleteCardPromptContent {...promptProps} card={card} />);
            } catch (err) {
                setIsUpdatingCard(false);
                return;
            }
            await props.cardsService.delete();
            setCard(null);
            props.onCardSelected(null);
            setIsUpdatingCard(false);
        } catch (err) {
            setIsUpdatingCard(false);
        }
    };

    /**
     * Calls props.onCardSelected when the card is clicked.
     *
     * @returns {void}
     */
    const handleCardSelected = (): void => {
        if (!props.onCardSelected) { return; }
        props.onCardSelected(card);
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (!card) {
        return (
          <section className="manage-payment-methods">
              <Message theme="danger" title="Please refresh the browser.">
                <p>Please reload this page, something has gone wrong.</p>
              </Message>
          </section>
        );
    }

    return (
        <section className="manage-payment-methods">
            <p>Which card should we charge your subscription to?</p>
            {!card.brand ? (
                <Message theme="info" title="Add a credit or debit card to your account.">
                    <p>Your card will be used for subscription payments and any other online purchases you make through this site, including event tickets and paid content submissions.</p>
                </Message>
            ) : null}

            {card.brand ? (
                <Message theme="info" title="Your Saved Card">
                    <p>This card will be used for subscription payments and any other online purchases you make through this site, including event tickets and paid content submissions.</p>
                </Message>
            ) : null}

            {card.brand ? (
                <PaymentCard
                    card={card}
                    disabled={isUpdatingCard}
                    onDelete={handleDeleteCard}
                    onClick={handleCardSelected}
                    selected={isEqual(props.selectedCard, card)}
                />
            ) : null}
            {!card.brand ? (
                <PaymentCardForm
                    onSubmit={handleNewCard}
                    stripeService={props.stripeService}
                    card={card}
                />
                )
            : null}
        </section>
    );
};
ManagePaymentCards.defaultProps = {
    cardsService,
    onCardSelected: () => null,
};
ManagePaymentCards.displayName = 'ManagePaymentCards';
